import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI, newsAPI } from '../services/api';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const [newsPosts, setNewsPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Check authentication
    if (!authAPI.isAuthenticated()) {
      navigate('/admin/login');
      return;
    }

    setUser(authAPI.getCurrentUser());
    loadNews();
  }, [navigate]);

  const loadNews = async () => {
    try {
      setLoading(true);
      const data = await newsAPI.getAllNewsAdmin();
      setNewsPosts(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    authAPI.logout();
    navigate('/admin/login');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this news post?')) {
      return;
    }

    try {
      await newsAPI.deleteNews(id);
      setNewsPosts(newsPosts.filter(post => post.id !== id));
    } catch (err) {
      alert('Error deleting post: ' + err.message);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <div className="container">
          <div className="header-content">
            <div>
              <h1>Admin Dashboard</h1>
              {user && <p>Welcome, {user.username}</p>}
            </div>
            <div className="header-actions">
              <button 
                className="btn btn-primary"
                onClick={() => navigate('/admin/news/create')}
              >
                + Create News Post
              </button>
              <button 
                className="btn btn-secondary"
                onClick={handleLogout}
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="dashboard-content">
        <div className="container">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {loading ? (
            <div className="loading">Loading news posts...</div>
          ) : (
            <div className="news-list">
              <h2>News Posts ({newsPosts.length})</h2>
              
              {newsPosts.length === 0 ? (
                <div className="empty-state">
                  <p>No news posts yet. Create your first post!</p>
                </div>
              ) : (
                <div className="news-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Image</th>
                        <th>Title (EN)</th>
                        <th>Title (AR)</th>
                        <th>Status</th>
                        <th>Author</th>
                        <th>Created</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {newsPosts.map(post => (
                        <tr key={post.id}>
                          <td>
                            {post.image_url ? (
                              <img 
                                src={`http://localhost:5000${post.image_url}`} 
                                alt={post.title_en}
                                className="post-thumbnail"
                              />
                            ) : (
                              <div className="no-image">No image</div>
                            )}
                          </td>
                          <td>{post.title_en}</td>
                          <td>{post.title_ar}</td>
                          <td>
                            <span className={`status-badge status-${post.status}`}>
                              {post.status}
                            </span>
                          </td>
                          <td>{post.author_name}</td>
                          <td>{formatDate(post.created_at)}</td>
                          <td>
                            <div className="action-buttons">
                              <button
                                className="btn-icon btn-edit"
                                onClick={() => navigate(`/admin/news/edit/${post.id}`)}
                                title="Edit"
                              >
                                ✏️
                              </button>
                              <button
                                className="btn-icon btn-delete"
                                onClick={() => handleDelete(post.id)}
                                title="Delete"
                              >
                                🗑️
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
